import BookOutlined from '@ant-design/icons/BookOutlined';
import BuildOutlined from '@ant-design/icons/BuildOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import MenuFoldOutlined from '@ant-design/icons/MenuFoldOutlined';
import MenuUnfoldOutlined from '@ant-design/icons/MenuUnfoldOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import Divider from 'antd/es/divider';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';

import { MenuContainer, MenuContent, StyledMenu } from './NavMenu.styles';
import ProjectSelect from './select/ProjectSelect';
import TeamSelect from './select/TeamSelect';

import { useBasePath, useProjectId, useTeamId } from '@utils/router';

const NavMenu = ({
  collapsed,
  setCollapsed,
  isDrawer,
  setDrawerOpen,
}: {
  collapsed: boolean;
  setCollapsed: (value: boolean | ((prev: boolean) => boolean)) => void;
  isDrawer?: boolean;
  setDrawerOpen?: Dispatch<SetStateAction<boolean>>;
}) => {
  const teamId = useTeamId();
  const projectId = useProjectId();
  const basePath = useBasePath();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const sidebarItems = useMemo(() => {
    return [
      {
        key: basePath,
        label: <Link to={basePath}>Home</Link>,
        icon: <HomeOutlined />,
      },
      {
        key: `${basePath}/conversations`,
        label: !projectId ? (
          'Conversations'
        ) : (
          <Link to={`${basePath}/conversations`}>Conversations</Link>
        ),
        icon: <MessageOutlined />,
        disabled: !projectId,
      },
      {
        key: `${basePath}/tasks`,
        label: !projectId ? (
          'Tasks'
        ) : (
          <Link to={`${basePath}/tasks`}>Tasks</Link>
        ),
        icon: <BuildOutlined />,
        disabled: !projectId,
      },
      {
        key: `${basePath}/library`,
        label: !projectId ? (
          'Library'
        ) : (
          <Link to={`${basePath}/library`}>Library</Link>
        ),
        icon: <BookOutlined />,
        disabled: !projectId,
      },
    ];
  }, [basePath, projectId]);

  // Ensure we highlight our path according to the route
  useEffect(() => {
    const path = location.pathname;
    let activeKey = '';
    for (const item of sidebarItems) {
      const { key } = item;
      if (path.startsWith(key) && key.length > activeKey.length) {
        activeKey = key;
      }
    }
    if (activeKey && !collapsed) {
      setOpenKeys([activeKey]);
    }
  }, [location.pathname, sidebarItems, teamId, collapsed, projectId]);

  const selectedKey = useMemo(() => {
    const path = location.pathname;
    const interpolatedKeys = sidebarItems
      .flatMap((item) => [item.key])
      .map((key) => key.replace(':teamId', teamId ?? ''))
      .sort((a, b) => b.length - a.length);
    return interpolatedKeys.find((key) => path.startsWith(key));
  }, [location.pathname, sidebarItems, teamId]);

  const isSelectedGroup = (itemKey: string) => {
    return selectedKey?.startsWith(itemKey) && selectedKey !== itemKey;
  };

  const handleCollapseClick = () => {
    setCollapsed((prev) => !prev);
  };

  return (
    <MenuContainer $collapsed={collapsed}>
      <TeamSelect collapsed={collapsed} />
      <ProjectSelect collapsed={collapsed} />
      <MenuContent>
        <StyledMenu
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          openKeys={openKeys}
          onOpenChange={(keys: string[]) => {
            setOpenKeys(keys);
          }}
          style={{
            borderInlineEnd: 'none',
            ...(isDrawer ? { background: 'unset' } : {}),
          }}
          onSelect={() => {
            setDrawerOpen?.(false);
          }}
          items={sidebarItems.map((item) => ({
            key: item.key,
            icon: item.icon,
            label: item.label,
            disabled: item.disabled,
            className: isSelectedGroup(item.key)
              ? 'ant-menu-submenu-selected'
              : '',
          }))}
        />
        {!isDrawer && (
          <div>
            <Divider />
            <StyledMenu
              mode="inline"
              style={{ borderInlineEnd: 'none' }}
              items={[
                {
                  key: 'collapse',
                  icon: collapsed ? (
                    <MenuUnfoldOutlined />
                  ) : (
                    <MenuFoldOutlined />
                  ),
                  label: collapsed ? 'Expand' : 'Collapse',
                  onClick: handleCollapseClick,
                },
              ]}
              selectable={false}
            />
          </div>
        )}
      </MenuContent>
    </MenuContainer>
  );
};

export default NavMenu;
